import { FC, FormEvent, useRef, useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import { useApolloClient } from '@apollo/client'
import { Form, Input } from 'antd'
import { useNavigate } from 'react-router-dom'

import { areAllRequiredFieldsFilled } from '~/lib/formHelpers'
import Box from '~/core/Box'
import Button from '~/core/Button'
import Text from '~/core/Text'
import { handleSubmit } from '../logic'
import { LoginErrorEnum, LoginErrorType } from '../types'
import {
    EMAIL_ADDRESS,
    EMAIL,
    PASSWORD,
    REQUIRED_FIELDS,
    REQUIRED_RULE,
} from '../constants'
import ResetPasswordLink from '../ResetPasswordLink'

import LoginErrorAlert from './LoginErrorAlert'

type LoginFormPropsType = {
    placement: string
    setMfaEnabled: (mfaEnabled: boolean) => void
    setSession: (session: string) => void
    setEmail: (email: string) => void
    initialEmailValue: string
}

const { useForm } = Form

const LoginForm: FC<LoginFormPropsType> = ({
    placement,
    setMfaEnabled,
    setSession,
    setEmail,
    initialEmailValue,
}) => {
    const navigate = useNavigate()
    const dispatch = useDispatch()
    const client = useApolloClient()
    const [form] = useForm()
    const { validateFields } = form

    const [loading, setLoading] = useState<boolean>(false)
    const [loginError, setLoginError] = useState<LoginErrorType | null>(null)
    const [hasError, setHasError] = useState<boolean>(false)
    const [formFilled, setFormFilled] = useState(false)

    const emailRef = useRef<any>(null)
    const passwordRef = useRef<any>(null)

    const checkForErrors = (field: string) => async () => {
        try {
            await validateFields([field])
            setHasError(false)
        } catch (err: any) {
            /*
             validateFields is async and a little wonky. This would normally
             always be an error, but we can get no errors with outOfDate: true
             when a password manager fills the form, so this solves for that
             case.
             */

            if (err?.errorFields?.length) {
                setHasError(true)
            } else {
                setHasError(false)
            }
        }
    }

    const handleFormSubmit = (evt: Event | FormEvent) => {
        evt?.preventDefault?.()
        if (loginError) {
            setLoginError(null)
        }
        handleSubmit({
            form,
            setLoading,
            setLoginError,
            setHasError,
            options: { dispatch, client },
            setMfaEnabled,
            setSession,
            setEmail,
            navigate,
        })
    }

    const handleSubmitFailed = () => {
        setHasError(true)
    }

    const handleChangedValues = () => {
        setFormFilled(areAllRequiredFieldsFilled(form, REQUIRED_FIELDS))
    }

    useEffect(() => {
        if (initialEmailValue) {
            passwordRef?.current?.focus()
        } else {
            emailRef?.current?.focus()
        }
    }, [initialEmailValue, emailRef, passwordRef])

    return (
        <Box alignItems="center" direction="col" width="100%">
            {loginError && (
                <LoginErrorAlert loginError={loginError as LoginErrorType} />
            )}
            <Form
                form={form}
                onFinish={handleFormSubmit}
                onFinishFailed={handleSubmitFailed}
                style={{ width: '100%' }}
                onValuesChange={handleChangedValues}
            >
                <Text
                    fontSize="14px"
                    color="blackAlpha65"
                    style={{ marginBottom: 0 }}
                >
                    Email address
                </Text>
                <Form.Item
                    name={EMAIL_ADDRESS}
                    validateTrigger="onBlur"
                    rules={REQUIRED_RULE(EMAIL)}
                    initialValue={initialEmailValue}
                >
                    <Input
                        data-test-target={EMAIL_ADDRESS}
                        placeholder="Name@company.com"
                        disabled={!!initialEmailValue}
                        ref={emailRef}
                        onBlur={checkForErrors(EMAIL_ADDRESS)}
                    />
                </Form.Item>
                <Box direction="colReverse">
                    <Form.Item
                        name={PASSWORD}
                        validateTrigger="onBlur"
                        rules={REQUIRED_RULE(PASSWORD)}
                    >
                        <Input
                            type={PASSWORD}
                            data-test-target={PASSWORD}
                            placeholder="Enter your password here"
                            ref={passwordRef}
                            onBlur={checkForErrors(EMAIL_ADDRESS)}
                        />
                    </Form.Item>
                    <Box justifyContent="between">
                        <Text fontSize="14px" color="blackAlpha65">
                            Password
                        </Text>
                        <ResetPasswordLink
                            placement={placement}
                            text="Reset your password"
                            disabled={
                                loginError?.type === LoginErrorEnum.LOCKED_OUT
                            }
                        />
                    </Box>
                </Box>
                <Form.Item name={`${placement}-submit`}>
                    <Box justifyContent="center">
                        <Button
                            disabled={hasError || loading || !formFilled}
                            data-test-target={`${placement}-submit`}
                            loading={loading}
                            type="primary"
                            size="large"
                            name="log-in"
                            placement={placement}
                            style={{ width: '167px' }}
                            htmlType="submit"
                            onClick={handleFormSubmit}
                        >
                            {loading ? 'Logging in' : 'Log in'}
                        </Button>
                    </Box>
                </Form.Item>
            </Form>
        </Box>
    )
}

export default LoginForm
